const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

/** API origin (where the backend is served) — used e.g. for the TON Connect manifest. */
export const apiOrigin = () => API_URL;

/** Build version baked at compile time (git SHA in prod, 'dev' locally). */
export const APP_VERSION: string = (import.meta.env.VITE_APP_VERSION as string) || 'dev';

/** The version currently deployed on the server (for the "new update" refresh prompt). */
export async function getAppVersion(): Promise<string | null> {
  try {
    const res = await fetch(`${API_URL}/v1/app/version`, { cache: 'no-store' });
    if (!res.ok) return null;
    const json = (await res.json().catch(() => null)) as { version?: string } | null;
    return json?.version ?? null;
  } catch {
    return null;
  }
}

export interface AuthResult {
  accessToken: string;
  refreshToken: string;
  user: { id: string; locale: string; ageVerified18: boolean; onboardingCompleted: boolean };
}

/* An HTTP failure that carries the response status, so the auth lifecycle can
   tell a definitive 401 (clear/refresh) from a transient network/5xx hiccup
   (keep the session, let the user retry). Preserves the API envelope code too. */
class HttpError extends Error {
  status: number;
  code: string | null;
  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new HttpError(
      err?.error?.message ?? `Request failed (${res.status})`,
      res.status,
      err?.error?.code ?? null,
    );
  }
  return res.json() as Promise<T>;
}

/** Exchange Telegram initData for a session (docs/15-auth-and-sessions.md). */
export function authTelegram(initData: string, ref?: string): Promise<AuthResult> {
  return post<AuthResult>('/v1/auth/telegram', { initData, ref });
}

/* ---- Session lifecycle (refresh-token-first; persisted in localStorage) ----
   localStorage survives a webview reload AND a full close/reopen, so the happy
   path on every relaunch is: refresh the rotating refresh token → fresh access.
   Telegram initData is single-use server-side, so we must NOT re-send it on a
   reload/retry — doing so 401s with "initData already used" (the lockout bug). */
const K_ACCESS = 'flm.access';
const K_REFRESH = 'flm.refresh';
const K_USER = 'flm.userId';

/* One-time back-compat migration: tokens used to live in sessionStorage. If a
   tab still has them there (mid-upgrade) and localStorage is empty, lift them
   over so the user isn't bounced to a fresh initData exchange. */
(function migrateLegacySession(): void {
  try {
    if (!localStorage.getItem(K_REFRESH) && sessionStorage.getItem('flm.refresh')) {
      const a = sessionStorage.getItem('flm.access');
      const r = sessionStorage.getItem('flm.refresh');
      const u = sessionStorage.getItem('flm.userId');
      if (a) localStorage.setItem(K_ACCESS, a);
      if (r) localStorage.setItem(K_REFRESH, r);
      if (u) localStorage.setItem(K_USER, u);
    }
    sessionStorage.removeItem('flm.access');
    sessionStorage.removeItem('flm.refresh');
    sessionStorage.removeItem('flm.userId');
  } catch {
    /* storage may be unavailable (private mode) — fall through to initData path */
  }
})();

export function setSession(s: { accessToken: string; refreshToken: string; userId?: string }): void {
  try {
    localStorage.setItem(K_ACCESS, s.accessToken);
    localStorage.setItem(K_REFRESH, s.refreshToken);
    if (s.userId) localStorage.setItem(K_USER, s.userId);
  } catch {
    /* ignore */
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(K_ACCESS);
    localStorage.removeItem(K_REFRESH);
    localStorage.removeItem(K_USER);
  } catch {
    /* ignore */
  }
}

export function accessToken(): string | null {
  try {
    return localStorage.getItem(K_ACCESS);
  } catch {
    return null;
  }
}

export function refreshTokenStored(): string | null {
  try {
    return localStorage.getItem(K_REFRESH);
  } catch {
    return null;
  }
}

/* Single-flight refresh: concurrent 401s (or boot + a queued call) must rotate
   the refresh token ONCE — a stampede would have each request rotate the token,
   and the loser's now-stale token trips the server's reuse detector (family
   revoke), logging the user out for real. */
let refreshInFlight: Promise<boolean> | null = null;

/** Rotate the stored refresh token → new access+refresh. Returns true on
    success. A definitive auth failure (401: invalid/expired/reused) clears the
    session so the caller falls back to initData; a transient/network failure
    keeps the session so a retry can succeed once connectivity returns. */
export function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const refreshToken = refreshTokenStored();
    if (!refreshToken) return false;
    try {
      const res = await post<{ accessToken: string; refreshToken: string }>('/v1/auth/refresh', {
        refreshToken,
      });
      setSession({ accessToken: res.accessToken, refreshToken: res.refreshToken });
      markAuthReady();
      return true;
    } catch (e) {
      // 401 = the refresh token is invalid/expired/reused — genuinely dead, so
      // drop it and let the boot flow fall back to initData. Anything else
      // (network down, 5xx) is transient: keep the session for a later retry.
      if (e instanceof HttpError && e.status === 401) clearSession();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/* Auth gate: early fire-and-forget calls (wizard saves, location) used to race
   the initData exchange and die with "not signed in" — now they wait for it. */
let resolveAuthReady: () => void = () => {};
const authReady = new Promise<void>((r) => (resolveAuthReady = r));
export function markAuthReady(): void {
  resolveAuthReady();
}

async function waitAuth(): Promise<void> {
  if (!accessToken()) {
    await Promise.race([authReady, new Promise((r) => setTimeout(r, 8000))]);
  }
}

async function authed<T>(method: string, path: string, body?: unknown): Promise<T> {
  await waitAuth();
  let token = accessToken();
  if (!token) throw new Error('not signed in');
  // content-type ONLY when a body travels: Fastify 400s body-less POSTs that
  // claim application/json ("Body cannot be empty…") — this silently broke
  // every body-less call (onboarding/complete, read receipts, boost, likes).
  const send = (bearer: string): Promise<Response> => {
    const headers: Record<string, string> = { authorization: `Bearer ${bearer}` };
    if (body !== undefined) headers['content-type'] = 'application/json';
    return fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  };

  let res = await send(token);
  // Access expired mid-session → rotate ONCE (single-flight) and retry the call.
  // Keeps long sessions alive without a reload; if refresh fails, surface auth.
  if (res.status === 401) {
    const ok = await refreshSession();
    if (!ok) {
      const err = await res.json().catch(() => null);
      throw new HttpError(err?.error?.message ?? 'not signed in', 401, err?.error?.code ?? null);
    }
    token = accessToken();
    if (!token) throw new Error('not signed in');
    res = await send(token);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new HttpError(
      err?.error?.message ?? `Request failed (${res.status})`,
      res.status,
      err?.error?.code ?? null,
    );
  }
  return res.json() as Promise<T>;
}

/** Optional dating attributes (all nullable). Shared by the owner profile
    (getMe) and the public profile (getProfile). The enum-like strings are
    constrained server-side to fixed option lists; languages is a free-ish
    string list. country/city are coarse place — never precise coordinates. */
export interface ProfileAttributes {
  heightCm?: number | null;
  weightKg?: number | null;
  bodyType?: string | null;
  smoking?: string | null;
  drinking?: string | null;
  exercise?: string | null;
  diet?: string | null;
  pets?: string | null;
  languages?: string[];
  education?: string | null;
  religion?: string | null;
  zodiac?: string | null;
  relationshipGoal?: string | null;
  wantsChildren?: string | null;
  country?: string | null;
  city?: string | null;
}

export interface MePayload {
  /** Admin-tunable per-profile photo cap (default 10) — wizard + editor slot count. */
  maxPhotos?: number;
  user: { id: string; locale: string; isPremium: boolean; ageVerified18: boolean; createdAt: string };
  telegram: { username?: string; firstName?: string; photoUrl?: string } | null;
  profile: ({
    displayName: string; age: number | null; gender: string; bio?: string; job?: string;
    isVerified: boolean; isHidden: boolean; boostedUntil?: string | null; interests: { slug: string; labelKey: string }[];
    photoCount: number; hasVoiceIntro: boolean; photos?: PhotoItem[];
    /** API-relative ("/v1/voice/<profileId>") — pass through mediaUrl(). */
    voiceUrl?: string | null; voiceDuration?: number | null;
  } & ProfileAttributes) | null;
  preference: { showGender: string; ageMin: number; ageMax: number; distanceKm: number } | null;
  onboarding: { step: number; completed: boolean };
  profileStrength: number;
}

/** Full public profile of another user (GET /v1/profiles/:userId) — identity +
    bio/job + interests + photos/voice + the optional dating attributes + coarse
    place. Never carries coordinates; photo/voice urls are API-relative. */
export interface PublicProfile extends ProfileAttributes {
  userId: string;
  displayName: string;
  age: number | null;
  gender: string;
  bio?: string | null;
  job?: string | null;
  isVerified: boolean;
  interests: { slug: string; labelKey: string }[];
  photos: PhotoItem[];
  voiceUrl?: string | null;
  voiceDuration?: number | null;
}

export const getMe = () => authed<MePayload>('GET', '/v1/me');
/** Hub stats — likes/sparks targeting me, my matches, gifts received (paid). */
export const getMyStats = () => authed<{ likes: number; matches: number; gifts: number }>('GET', '/v1/me/stats');

/** Current subscription + effective entitlements for the Manage-plan view.
    isPremium follows the active Subscription (status active + future expiry) the
    admin grant / payments rail write; entitlements describe the per-day caps the
    live gates enforce ('unlimited' once premium). daysRemaining = ceil days, 0 free. */
export interface SubscriptionPayload {
  isPremium: boolean;
  /** icon: the plan's icon key (drives the premium badge glyph); null falls back to crown. */
  plan: { id: string; name: string; perks: string[]; icon?: string | null } | null;
  status: string | null;
  startedAt: string | null;
  expiresAt: string | null;
  daysRemaining: number;
  /** Effective per-tier entitlements (active plan merged over free-tier settings).
      superLikesPerDay is now the plan's Spark NUMBER (no blanket 'unlimited'); the
      added boostsPerWeek / messageBeforeMatch drive the free-Boost CTA + perk copy. */
  entitlements: {
    likesPerDay: number | 'unlimited';
    messagesPerDay: number | 'unlimited';
    superLikesPerDay: number | 'unlimited';
    boostsPerWeek: number;
    canRewind: boolean;
    canSeeLikers: boolean;
    messageBeforeMatch: boolean;
    boostDurationMin: number;
  };
  /** Today's consumption against the caps (boosts counted this ISO week) so the
      app can show "X of N left". */
  usage: { yesToday: number; sparkToday: number; boostsThisWeek: number };
  /** ISO timestamp of the next ISO-week boundary — when boostsThisWeek resets to
      0 — so the Boost row can show "resets in N days / on <date>". */
  boostsResetAt?: string | null;
}
export const getSubscription = () => authed<SubscriptionPayload>('GET', '/v1/me/subscription');
/** Daily-claim: streak + claimable + reward. */
export const getDaily = () =>
  authed<{ streak: number; claimable: boolean; nextClaimInMs: number; reward: { sparks: number; likes: number } }>('GET', '/v1/me/daily');
export const claimDaily = () =>
  authed<{ ok: boolean; alreadyClaimed?: boolean; streak: number; nextClaimInMs: number; reward?: { sparks: number; likes: number } }>('POST', '/v1/me/daily/claim');
/** Invite link token + counts + configured reward for the Invite & earn card. */
export const getReferrals = () =>
  authed<{
    startParam: string;
    invited: number;
    rewarded: number;
    enabled: boolean;
    rewardKind: 'sparks' | 'premium';
    rewardPerInvite?: number;
    premiumDays?: number;
    planName?: string;
  }>('GET', '/v1/me/referrals');

/** Pending celebratory rewards (referral / trial) — read once, then cleared. */
export interface RewardItem {
  kind: 'referral' | 'trial';
  planName?: string;
  days?: number;
  sparks?: number;
}
export const getRewards = () => authed<{ items: RewardItem[] }>('GET', '/v1/me/rewards');
export const patchOnboarding = (step: number, data: Record<string, unknown>) =>
  authed('PATCH', '/v1/me/onboarding', { step, data });
export const completeOnboarding = () => authed<MePayload>('POST', '/v1/me/onboarding/complete');
export const patchProfile = (data: Record<string, unknown>) => authed<MePayload>('PATCH', '/v1/me/profile', data);
export const putPreferences = (data: Record<string, unknown>) => authed<MePayload>('PUT', '/v1/me/preferences', data);
export const putInterests = (slugs: string[]) => authed('PUT', '/v1/me/interests', { slugs });
/** Interest taxonomy (seeded) — the API answers a bare array, not `{ items }`. */
export const getInterests = () => authed<{ slug: string; labelKey: string }[]>('GET', '/v1/interests');
/** Another user's full public profile (identity + bio/job + interests +
    photos/voice + optional dating attributes + coarse place). Lazy-fetched by
    the detail overlay to enrich the deck card. 404 → hidden/inactive/blocked. */
export const getProfile = (userId: string) => authed<PublicProfile>('GET', `/v1/profiles/${userId}`);

/* ---- media / photos ---- */
/** Absolute URL for an API-relative media path ("/v1/photos/<id>"); null when absent. */
/** Absolute media URL; pass `w` to request a resized variant (?w=<width>) for
    thumbnails/avatars/map — the server resizes + caches. Omit for full-res. */
export const mediaUrl = (p?: string | null, w?: number) =>
  p ? `${API_URL}${p}${w ? `${p.includes('?') ? '&' : '?'}w=${w}` : ''}` : null;

export interface PhotoItem { id: string; url: string; isCover: boolean; blurHash?: string | null }

/** Multipart upload — Bearer only; the browser sets the multipart content-type. */
export async function uploadPhoto(file: File): Promise<PhotoItem> {
  await waitAuth();
  const token = accessToken();
  if (!token) throw new Error('not signed in');
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/v1/me/photos`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.error?.message ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<PhotoItem>;
}

export const getMyPhotos = () => authed<{ items: PhotoItem[] }>('GET', '/v1/me/photos');
export const deletePhoto = (id: string) => authed<{ ok: boolean }>('DELETE', `/v1/me/photos/${id}`);

/* ---- media / voice intro ---- */
/** Multipart voice-intro upload — duration travels as a form field BEFORE the
    file part so the server sees it when the file resolves (webm/ogg/mp4 ≤1.5MB, 1–12s). */
export async function uploadVoice(blob: Blob, durationSecs: number): Promise<{ url: string; durationSecs: number }> {
  await waitAuth();
  const token = accessToken();
  if (!token) throw new Error('not signed in');
  const form = new FormData();
  form.append('duration', String(Math.round(durationSecs)));
  form.append('file', blob, 'voice');
  const res = await fetch(`${API_URL}/v1/me/voice`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.error?.message ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<{ url: string; durationSecs: number }>;
}

export const deleteVoice = () => authed<{ ok: boolean }>('DELETE', '/v1/me/voice');

/* ---- discovery / matching ---- */
export interface ApiCard {
  userId: string; name: string; age: number; gender: string; bio?: string; job?: string;
  verified: boolean; online: boolean; interests: string[]; common: number; distKm: number | null;
  photoUrl?: string | null; photos?: string[];
  /** BlurHash LQIP placeholders: blurHash = cover, photoBlurs aligned to photos[]. */
  blurHash?: string | null; photoBlurs?: (string | null)[];
  /** Coarse place (never coordinates) — the card location-line fallback when
      distKm is null. country is ISO-3166 alpha-2 (mapped to a display name client-side). */
  city?: string | null; country?: string | null;
  /** Candidate's premium status — drives the subtle gold-crown badge on the card. */
  isPremium?: boolean;
  /** API-relative ("/v1/voice/<profileId>") — pass through mediaUrl(). voice = duration secs. */
  voiceUrl?: string | null; voice?: number;
}
export const getDeck = (limit = 20) => authed<{ items: ApiCard[] }>('GET', `/v1/discovery/deck?limit=${limit}`);
export const postSwipe = (targetUserId: string, decision: 'pass' | 'yes' | 'spark') =>
  authed<{ match: boolean; matchId?: string; conversationId?: string; profile?: ApiCard }>('POST', '/v1/swipes', { targetUserId, decision });
/** Premium "Rewind": undo my most recent NON-MATCH swipe. Resolves to {ok:true,
    profile} (re-show that card at the front of the deck), or {ok:false, reason} —
    'nothing_to_undo' (no prior swipe) or 'matched' (the swipe matched; the match,
    conversation and messages are left intact — Rewind never destroys them).
    Free users get a 403 (code 'auth/forbidden') — thrown as ApiCodedError so the
    deck can route the premium gate to onUpgrade instead of regexing the message. */
export async function rewindSwipe(): Promise<{ ok: boolean; profile?: ApiCard; reason?: string }> {
  await waitAuth();
  const token = accessToken();
  if (!token) throw new ApiCodedError('not signed in', null);
  const res = await fetch(`${API_URL}/v1/swipes/rewind`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new ApiCodedError(
      err?.error?.message ?? `Request failed (${res.status})`,
      err?.error?.code ?? null,
    );
  }
  return res.json() as Promise<{ ok: boolean; profile?: ApiCard; reason?: string }>;
}
export const getMatches = () =>
  authed<{ items: { matchId: string; conversationId: string; matchedAt: string; profile: ApiCard }[] }>('GET', '/v1/matches');
export const getLikesCount = () => authed<{ count: number }>('GET', '/v1/likes/count');
export const getLikes = () =>
  authed<{ items: { likedAt: string; spark: boolean; profile: ApiCard }[] }>('GET', '/v1/likes');
/** Save my PostGIS point. The server reverse-geocodes the coords (best-effort,
    cached) and echoes back the coarse place ({country: ISO-2, city}) so the
    wizard can confirm "City, Country" after a GPS fix or map pin; either may be
    null when Nominatim is unreachable. */
export const putLocation = (lat: number, lng: number, precisionM?: number, source: 'gps' | 'manual' = 'gps') =>
  authed<{ ok: boolean; country?: string | null; city?: string | null }>(
    'PUT',
    '/v1/me/location',
    { lat, lng, precisionM, source },
  );

/* ---- location: skip-GPS country/city fallback ---- */
export interface PlaceLocation {
  ok: boolean; lat: number; lng: number; city: string | null; country: string;
  /** which path produced the coords: live geocode, cache hit, or country centroid */
  via: 'nominatim' | 'nominatim-cache' | 'centroid';
}
/** Resolve country (+optional city) to real coords server-side (Nominatim, cached;
    bundled country-centroid fallback) and store it as my location. */
export const setLocationByPlace = (country: string, city?: string) =>
  authed<PlaceLocation>('POST', '/v1/me/location/place', { country, ...(city ? { city } : {}) });
/** Bundled country list for the picker ([{code,name}], sorted by name; public, cached). */
export const getCountries = () =>
  authed<{ items: { code: string; name: string }[] }>('GET', '/v1/geo/countries');

/* ---- boost: claim a FREE weekly boost (per plan entitlements.boostsPerWeek).
   Resolves with the new active window + remaining free boosts on success; throws
   the auth/forbidden envelope when the free allowance is exhausted / the tier has
   none → caller routes to the paid Boost purchase. ---- */
export const postBoost = () =>
  authed<{ boostedUntil: string; boostsRemaining?: number }>('POST', '/v1/me/boost');

/* ---- map / nearby ---- */
/* PRIVACY: each person's lat/lng is SNAPPED server-side to a coarse grid (cell ≈
   areaRadiusM) — a soft "in this area" zone, never an exact pin. distKm is the
   real distance (fine to show); the precise point never leaves the server. */
export interface NearbyItem {
  userId: string; name: string; verified: boolean; online: boolean;
  /** Drives the gold-crown premium badge on the opened profile (deck parity). */
  isPremium?: boolean;
  /** API-relative ("/v1/photos/<id>") — pass through mediaUrl(). */
  photoUrl: string | null;
  /** lat/lng are the grid-snapped zone center (NOT the exact location). */
  distKm: number; lat: number; lng: number;
}
export const getNearby = () =>
  authed<{
    center: { lat: number; lng: number } | null;
    /** Radius (m) of the translucent area circle to draw per person (admin-tunable; default 1000). */
    areaRadiusM?: number;
    items: NearbyItem[];
  }>('GET', '/v1/map/nearby');

/* ---- chat ---- */
export interface ApiConversation {
  conversationId: string; matchId: string; matchedAt: string; profile: ApiCard;
  lastMessage: { body: string | null; kind: string; sentAt: string; mine: boolean } | null;
  unread: number;
}
export interface ApiMessage {
  id: string; mine: boolean; kind: string; body: string | null;
  /** API-relative ("/v1/chat/images/<id>") for kind='image' — pass through mediaUrl(). */
  imageUrl?: string | null;
  sentAt: string;
  deliveredAt: string | null; readAt: string | null;
}
export const getConversations = () => authed<{ items: ApiConversation[] }>('GET', '/v1/conversations');
export const getMessages = (conversationId: string, cursor?: string) =>
  authed<{ items: ApiMessage[]; nextCursor: string | null }>(
    'GET',
    `/v1/conversations/${conversationId}/messages${cursor ? `?cursor=${cursor}` : ''}`,
  );
export const sendMessage = (conversationId: string, body: string, clientMsgId: string) =>
  authed<{ id: string; sentAt: string; duplicate: boolean }>(
    'POST',
    `/v1/conversations/${conversationId}/messages`,
    { body, clientMsgId },
  );
export const markRead = (conversationId: string) =>
  authed<{ read: number }>('POST', `/v1/conversations/${conversationId}/read`);

/** Send a photo message — Bearer only (the browser sets the multipart content-type).
    The blob is the client-compressed JPEG (optimizeImage: ≤1600px, EXIF stripped).
    clientMsgId travels as a form field BEFORE the file part so the server sees it
    on the file resolve (idempotent retries return the original message). */
export async function sendImageMessage(
  conversationId: string,
  blob: Blob,
  clientMsgId: string,
): Promise<{ id: string; kind: string; imageUrl: string; sentAt: string; duplicate: boolean }> {
  await waitAuth();
  const token = accessToken();
  if (!token) throw new ApiCodedError('not signed in', null);
  const form = new FormData();
  form.append('clientMsgId', clientMsgId);
  form.append('file', blob, 'image.jpg');
  const res = await fetch(`${API_URL}/v1/conversations/${conversationId}/images`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new ApiCodedError(
      err?.error?.message ?? `Request failed (${res.status})`,
      err?.error?.code ?? null,
    );
  }
  return res.json() as Promise<{ id: string; kind: string; imageUrl: string; sentAt: string; duplicate: boolean }>;
}

/* ---- safety: block / report / unmatch (docs/05 §6.8) ---- */
export type ReportReason =
  | 'spam' | 'harassment' | 'nudity' | 'underage' | 'scam'
  | 'impersonation' | 'off_platform_solicitation' | 'fake_or_catfish' | 'other';

/** Block = mutual invisibility; silently closes any match (conversation goes with it). */
export const blockUser = (userId: string) =>
  authed<{ ok: boolean; unmatched: boolean }>('POST', '/v1/blocks', { userId });
export const unblockUser = (userId: string) =>
  authed<{ ok: boolean }>('DELETE', `/v1/blocks/${userId}`);
/** Opens a moderation report — the server also blocks the target in the same tx. */
export const reportUser = (userId: string, reason: ReportReason, detail?: string) =>
  authed<{ ok: boolean }>('POST', '/v1/reports', { targetUserId: userId, reason, detail });
/** Unmatch (participants only) — deletes the match; conversation/messages cascade. */
export const unmatch = (matchId: string) =>
  authed<{ ok: boolean }>('DELETE', `/v1/matches/${matchId}`);

/* ---- payments (TON checkout) ---- */
export interface PaymentInvoice {
  paymentId: string;
  address: string;
  /** 'testnet' | 'mainnet' — passed to TON Connect so it signs on the right chain. */
  network?: 'testnet' | 'mainnet';
  amountNano: string;
  amountTon: string;
  memo: string;
  validUntil: string;
  /** base64 comment-cell BOC → TON Connect sendTransaction message payload (memo on-chain). */
  payloadBase64?: string;
  /** "ton://transfer/..." deep link — carries address, amount and memo (QR / manual fallback). */
  payUrl: string;
}
export type PaymentStatus = 'pending' | 'paid' | 'underpaid' | 'expired';

/** Telegram Stars invoice — open starsLink (Telegram shows its native Stars sheet). */
export interface StarsInvoice {
  paymentId: string;
  starsLink: string;
}

export const createInvoice = (
  planId: 'spark' | 'pulse' | 'infinity',
  provider: 'ton_connect' | 'stars' = 'ton_connect',
  period: 'monthly' | 'yearly' = 'monthly',
) =>
  authed<Partial<PaymentInvoice> & { paymentId: string; starsLink?: string }>(
    'POST',
    '/v1/payments/invoices',
    { planId, provider, period },
  );
export const createGiftInvoice = (giftId: string, recipientUserId: string, conversationId?: string) =>
  authed<PaymentInvoice>('POST', '/v1/payments/gift-invoices', { giftId, recipientUserId, conversationId });
/** One-off Boost invoice (consumable; admin-priced). TON → PaymentInvoice (payUrl + memo,
    verified via checkPayment); Stars → { paymentId, starsLink } (bot settle applies boostedUntil). */
export const createBoostInvoice = (provider: 'ton_connect' | 'stars' = 'ton_connect') =>
  authed<Partial<PaymentInvoice> & { paymentId: string; starsLink?: string }>(
    'POST',
    '/v1/payments/boost-invoices',
    { provider },
  );
export const checkPayment = (paymentId: string) =>
  authed<{ status: PaymentStatus }>('POST', `/v1/payments/${paymentId}/check`);

/* ---- catalog ---- */
/** Purchasable plans (the non-purchasable 'free' default tier is excluded server-side).
    Each tier carries MONTHLY (priceTon/Usd/Stars) + optional YEARLY (…Yearly, null when
    unset) prices for the monthly/yearly toggle + %-off badge, plus a per-plan icon key. */
export const getPlans = () =>
  authed<{ items: {
    id: string; name: string;
    priceTon: string; priceUsd: number; priceStars: number;
    priceTonYearly: string | null; priceUsdYearly: number | null; priceStarsYearly: number | null;
    periodMonths: number; isFeatured: boolean; icon: string | null; perks: string[];
  }[] }>('GET', '/v1/plans');
export const getGifts = () =>
  authed<{ items: { id: string; name: string; priceTon: string; priceStars: number }[] }>('GET', '/v1/gifts');
export const getEvents = () =>
  authed<{ items: { id: string; title: string; venue: string; startsAt: string; goingCount: number; depositTon: string | null; myRsvp: EventRsvpStatus | null }[] }>('GET', '/v1/events');

/* ---- events RSVP (free → going; deposit → TON invoice verified on-chain via checkPayment) ---- */
export type EventRsvpStatus = 'pending' | 'going' | 'cancelled';

/** Free events answer {status:'going'}; deposit events answer a PaymentInvoice (payUrl + memo). */
export const rsvpEvent = (eventId: string) =>
  authed<{ status?: 'going' } & Partial<PaymentInvoice>>('POST', `/v1/events/${eventId}/rsvp`);
export const cancelRsvp = (eventId: string) =>
  authed<{ status: 'cancelled' }>('DELETE', `/v1/events/${eventId}/rsvp`);

/* ---- social: Pulse feed + stories ---- */
export interface FeedAuthor {
  userId: string; name: string; job: string; verified: boolean; online: boolean;
  /** API-relative ("/v1/photos/<id>") — pass through mediaUrl(). */
  photoUrl: string | null;
}
export interface FeedPost {
  id: string; author: FeedAuthor; text: string; likeCount: number; likedByMe: boolean; createdAt: string;
}
export interface StoryGroup {
  author: FeedAuthor;
  /** url is API-relative ("/v1/stories/<id>/media") — pass through mediaUrl(). */
  stories: { id: string; url: string; createdAt: string; expiresAt: string }[];
}
export const getFeed = () => authed<{ items: FeedPost[] }>('GET', '/v1/feed');
export const createPost = (text: string) => authed<FeedPost>('POST', '/v1/posts', { text });
export const togglePostLike = (id: string) =>
  authed<{ liked: boolean; likeCount: number }>('POST', `/v1/posts/${id}/like`);
export const getStories = () => authed<{ items: StoryGroup[] }>('GET', '/v1/stories');

/** Multipart story upload — Bearer only; the browser sets the multipart content-type. */
export async function uploadStory(file: File): Promise<{ id: string; url: string; createdAt: string; expiresAt: string }> {
  await waitAuth();
  const token = accessToken();
  if (!token) throw new Error('not signed in');
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/v1/stories`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.error?.message ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<{ id: string; url: string; createdAt: string; expiresAt: string }>;
}

/* ---- i18n: admin-managed translation overrides (public, no auth) ---- */
export async function getI18nOverrides(locale: string): Promise<Record<string, string>> {
  const res = await fetch(`${API_URL}/v1/i18n/${encodeURIComponent(locale)}`);
  if (!res.ok) return {};
  const json = (await res.json().catch(() => null)) as { entries?: Record<string, string> } | null;
  return json?.entries ?? {};
}

/* ---- chat: code-aware send (free daily message cap → premium upsell) ---- */
/** Error that keeps the API envelope code (e.g. "common/rate_limited") so the
    thread can route limit refusals to the upgrade flow instead of regexing
    human messages. */
export class ApiCodedError extends Error {
  code: string | null;
  constructor(message: string, code: string | null) {
    super(message);
    this.code = code;
  }
}

/** Like sendMessage, but failures throw ApiCodedError carrying error.code —
    the chat thread shows its in-thread premium upsell on common/rate_limited. */
export async function sendMessageCoded(
  conversationId: string,
  body: string,
  clientMsgId: string,
): Promise<{ id: string; sentAt: string; duplicate: boolean }> {
  await waitAuth();
  const token = accessToken();
  if (!token) throw new ApiCodedError('not signed in', null);
  const res = await fetch(`${API_URL}/v1/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ body, clientMsgId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new ApiCodedError(
      err?.error?.message ?? `Request failed (${res.status})`,
      err?.error?.code ?? null,
    );
  }
  return res.json() as Promise<{ id: string; sentAt: string; duplicate: boolean }>;
}

/* ---- likes paywall teaser (free tier) ---- */
/** Up to 6 anonymous pending likers — photo ONLY (no ids/names/ages). The
    likes overlay blurs them client-side; server-side pre-blurred thumbnails
    are the hardening follow-up. photoUrl is API-relative — pass through mediaUrl(). */
export const getLikesPreview = () =>
  authed<{ items: { photoUrl: string | null }[] }>('GET', '/v1/likes/preview');

/* ---- message requests: Infinity "message before match" inbox ----
   The sender (discover screen) posts a request gated on the messageBeforeMatch
   entitlement (403 auth/forbidden otherwise → route to the premium sheet). The
   recipient owns accept/decline: accept materializes a Match + Conversation
   seeded with the opener and returns the new conversationId. */
export interface ApiMessageRequest {
  id: string;
  bodyText: string;
  createdAt: string;
  profile: ApiCard;
}
/** Send a pre-match message request. 403 'auth/forbidden' when the sender lacks
    the Infinity perk; 409 'common/conflict' when already matched / a request
    exists / blocked / self. Throws ApiCodedError so the caller can route the
    perk gate to the premium sheet instead of regexing the message. */
export async function sendMessageRequest(
  toUserId: string,
  bodyText: string,
): Promise<{ ok: boolean; id: string }> {
  await waitAuth();
  const token = accessToken();
  if (!token) throw new ApiCodedError('not signed in', null);
  const res = await fetch(`${API_URL}/v1/requests`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ toUserId, bodyText }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new ApiCodedError(
      err?.error?.message ?? `Request failed (${res.status})`,
      err?.error?.code ?? null,
    );
  }
  return res.json() as Promise<{ ok: boolean; id: string }>;
}
/** My incoming PENDING message requests (each with the sender's card). */
export const getMessageRequests = () =>
  authed<{ items: ApiMessageRequest[] }>('GET', '/v1/requests');
/** Accept → create the match/conversation seeded with the opener; returns the conversationId. */
export const acceptMessageRequest = (id: string) =>
  authed<{ ok: boolean; conversationId: string }>('POST', `/v1/requests/${id}/accept`);
/** Decline → status=declined (no conversation). */
export const declineMessageRequest = (id: string) =>
  authed<{ ok: boolean }>('POST', `/v1/requests/${id}/decline`);
